import { BoxProps, ListItem } from "arkh";
import React, { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";

import { SelectList } from "arkh/src/cross-platform/SelectList/SelectList";

import { TeamSelectList_Team$key } from "./__generated__/TeamSelectList_Team.graphql";
import { TeamIconCircle } from "../../mobile/src/components/mobile/TeamIconCircle";
import { TeamSelectListItem_Team$key } from "./__generated__/TeamSelectListItem_Team.graphql";

export function TeamSelectList(props: {
  disabled?(_: string): boolean;
  insets: BoxProps["insets"];
  teams: TeamSelectList_Team$key;
  selected: string[];

  contentBox?: BoxProps;
  ListHeaderComponent?: React.ReactElement;
  ListFooterComponent?: React.ReactElement;
  onItemPress(itemId: string, selected: boolean): void;
  readOnly?: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment TeamSelectList_Team on Team @relay(plural: true) {
        id
        name
        isPrivate
        ...TeamSelectListItem_Team
      }
    `,
    props.teams
  );

  const teams = useMemo(
    () =>
      data.slice().sort((a, b) => {
        const aP = a.isPrivate ? 1 : 0;
        const bP = b.isPrivate ? 1 : 0;
        if (bP - aP === 0) {
          return a.name > b.name ? 1 : -1;
        }
        return bP - aP;
      }),
    [data]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof teams)[number]; index: number }) => {
      const selected = props.selected.includes(item.id);
      return (
        <TeamSelectListItem
          notSelectable={props.disabled?.(item.id)}
          selected={selected}
          team={item}
          onPress={() => props.onItemPress(item.id, selected)}
          isFirst={index === 0}
          isLast={index === teams.length - 1}
        />
      );
    },
    [props, teams.length]
  );

  return (
    <SelectList
      contentBox={props.contentBox}
      insets={props.insets}
      ListHeaderComponent={props.ListHeaderComponent}
      ListFooterComponent={props.ListFooterComponent}
      data={teams}
      renderItem={renderItem}
    />
  );
}

export function TeamSelectListItem(props: {
  notSelectable?: boolean;
  team: TeamSelectListItem_Team$key;
  selected: boolean;
  onPress: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  readOnly?: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment TeamSelectListItem_Team on Team {
        id
        name
        ...TeamIconCircleComponent_Team
      }
    `,
    props.team
  );

  const showSelectIndicator = useMemo(() => {
    if (props.readOnly) {
      return props.selected && !props.notSelectable;
    }

    return !props.notSelectable;
  }, [props.readOnly, props.notSelectable, props.selected]);

  return (
    <ListItem
      disabled={props.readOnly}
      isFirst={props.isFirst}
      border="border-primary"
      isLast={props.isLast}
      onPress={props.onPress}
      left={<TeamIconCircle team={data} size="smallMedium" />}
      right={
        showSelectIndicator ? (
          <ListItem.SelectIndicator selected={props.selected} />
        ) : null
      }
    >
      <ListItem.Title>{data.name}</ListItem.Title>
    </ListItem>
  );
}

export function TeamSelectListWholeStoreOption(props: {
  selected: boolean;
  onSelect(): void;
  disabled?: boolean;
}) {
  return (
    <ListItem
      right={<ListItem.SelectIndicator selected={props.selected} />}
      isFree
      onPress={props.onSelect}
      border="border-primary"
      disabled={props.disabled}
    >
      <ListItem.Title>
        <FormattedMessage
          description="TeamSelectList entire store"
          defaultMessage="All teams"
        />
      </ListItem.Title>
    </ListItem>
  );
}
